import { RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';

import { RunMark } from './RunMark';

/**
 * What a status line becomes when it is not "working on it".
 *
 * Queued: this question waits for another of the user's answers, which may be
 * in a different conversation. Say which one, and offer to open it.
 *
 * Didn't finish: a status line is stored with the thread, so one that is still
 * there when nothing is running belongs to an answer that never came — the
 * service restarted, or the turn died. It used to keep its spinner or show
 * "Running the analysis…" forever, which reads as "still working". Say what
 * happened and offer the question again.
 *
 * English always: this is the system talking, not an answer.
 */

export function QueuedNotice({
  waitingFor,
  currentThreadId
}: {
  waitingFor?: { thread_id?: string | null; title?: string } | null;
  currentThreadId?: string | null;
}) {
  const other =
    waitingFor?.thread_id && waitingFor.thread_id !== currentThreadId
      ? waitingFor.thread_id
      : null;
  return (
    <div
      role="status"
      className="inline-flex max-w-full items-center gap-2 rounded-xl border border-dashed border-border px-3 py-2 text-[13px] text-muted-foreground"
    >
      <RunMark state="queued" />
      {other ? (
        <span className="min-w-0">
          Starts when{' '}
          <b className="font-semibold text-foreground" dir="auto">
            {waitingFor?.title || 'your other conversation'}
          </b>{' '}
          finishes
        </span>
      ) : (
        <span>Starts when the current answer finishes</span>
      )}
      {other ? (
        <Link
          to={`/thread/${other}`}
          className="shrink-0 font-semibold text-foreground underline underline-offset-2"
        >
          Open
        </Link>
      ) : null}
    </div>
  );
}

export function DidNotFinish({ onAskAgain }: { onAskAgain?: () => void }) {
  return (
    <div className="w-full max-w-xl rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 dark:border-red-900/60 dark:bg-red-950/30">
      <div className="flex items-center gap-2 text-[13px] font-semibold text-red-600 dark:text-red-400">
        <RunMark state="failed" />
        This answer didn't finish
      </div>
      <p className="mt-1 text-[13.5px] text-muted-foreground">
        It stopped before it was ready. Your question is saved
        {onAskAgain ? ' — ask it again with one tap.' : '.'}
      </p>
      {onAskAgain ? (
        <button
          type="button"
          onClick={onAskAgain}
          className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-foreground px-3.5 py-1.5 text-[13px] font-semibold text-background hover:opacity-90"
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
          Ask again
        </button>
      ) : null}
    </div>
  );
}
